"use client";

import { CircleCheck as CheckCircle2, Search, MapPin, Globe } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Pricing() {
  const rooms = [{
    numeral: "01",
    name: "Website Design",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704907/img/web_design_service.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704907/img/web_design_service.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704907/img/web_design_service.png-wvc-sizes",
    alt: "Modern website design interface on laptop screen",
    sqft: "Custom Build",
    guests: "Full Control",
    view: "Responsive",
    bed: "SEO Ready",
    description: "We craft high-converting, visually stunning websites tailored for small businesses. Your digital storefront, optimized for speed and user experience.",
    amenities: [{
      icon: Globe,
      label: "Mobile Optimized"
    }, {
      icon: CheckCircle2,
      label: "Fast Loading"
    }, {
      icon: CheckCircle2,
      label: "Modern UI/UX"
    }],
    price: "$1,500"
  }, {
    numeral: "02",
    name: "SEO Strategy",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704907/img/seo_strategy_service.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704907/img/seo_strategy_service.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704907/img/seo_strategy_service.png-wvc-sizes",
    alt: "SEO analytics dashboard showing growth",
    sqft: "Data Driven",
    guests: "Organic Growth",
    view: "Rank Higher",
    bed: "Keyword Focus",
    description: "Dominate search results with our comprehensive SEO strategies. We help your business get discovered by the customers who need you most.",
    amenities: [{
      icon: Search,
      label: "Keyword Research"
    }, {
      icon: CheckCircle2,
      label: "Content Strategy"
    }, {
      icon: CheckCircle2,
      label: "Performance Tracking"
    }],
    price: "$800"
  }, {
    numeral: "03",
    name: "Local SEO",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704907/img/local_seo_service.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704907/img/local_seo_service.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704907/img/local_seo_service.png-wvc-sizes",
    alt: "Google Maps listing showing local business location",
    sqft: "Local Reach",
    guests: "Google Maps",
    view: "Listing Fixes",
    bed: "Review Mgmt",
    description: "Specializing in Google Business Profile recovery and local ranking. We fix suspensions and ensure your business shines in local search results.",
    amenities: [{
      icon: MapPin,
      label: "Google Maps Fix"
    }, {
      icon: CheckCircle2,
      label: "Review Strategy"
    }, {
      icon: CheckCircle2,
      label: "NAP Consistency"
    }],
    price: "$500"
  }];
  return;
}
function PageIntro() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} className={`max-w-4xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="flex items-center gap-4 mb-10">
        <span className="font-default text-xs tracking-[0.3em] uppercase text-primary">
          Our Services
        </span>
        <span className="h-px w-12 bg-primary" />
      </div>

      <h1 className="font-default font-bold leading-[1.05] tracking-tight text-foreground" style={{
      fontSize: "clamp(2.75rem, 6vw, 5.5rem)"
    }}>
        Digital Growth.
        <br />
        <span className="italic font-normal text-primary">
          For Local Business.
        </span>
      </h1>

      <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed font-default text-muted-foreground">
        Asahi Search provides targeted marketing solutions designed to help small and medium-sized businesses thrive online. From building your brand to fixing your Google presence, we are your growth partner.
      </p>
    </div>;
}
type Room = {
  numeral: string;
  name: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  sqft: string;
  guests: string;
  view: string;
  bed: string;
  description: string;
  amenities: {
    icon: React.ComponentType<{
      className?: string;
    }>;
    label: string;
  }[];
  price: string;
};
function RoomListing({
  room,
  index
}: {
  room: Room;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isReversed = index % 2 === 1;
  return <div ref={ref} data-index={index} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className={`lg:col-span-7 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
        <div className="group relative overflow-hidden rounded-lg shadow-lg">
          <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg">
            <img src={room.image} data-wvc-srcset={room.srcset} data-wvc-sizes={room.sizes} alt={room.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
          </div>
        </div>
      </div>

      <div className={`lg:col-span-5 ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
        <div className="flex flex-col">
          <span className="font-default italic text-sm tracking-[0.2em] text-primary">
            {room.numeral}
          </span>

          <h2 className="mt-4 font-default font-bold leading-[1.05] tracking-tight text-foreground" style={{
          fontSize: "clamp(2.5rem, 4.5vw, 4rem)"
        }}>
            {room.name}
          </h2>

          <div className="mt-8 h-px w-16 bg-primary" />

          <dl className="mt-8 grid grid-cols-2 gap-x-8 gap-y-6">
            <MetaItem label="Build Type" value={room.sqft} />
            <MetaItem label="Control" value={room.guests} />
            <MetaItem label="Optimization" value={room.view} />
            <MetaItem label="Focus" value={room.bed} />
          </dl>

          <p className="mt-10 font-default italic font-light text-lg leading-relaxed text-muted-foreground">
            {room.description}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            {room.amenities.map((amenity, i) => {
            const Icon = amenity.icon;
            return <div key={i} data-index={i} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" style={{
                strokeWidth: 1.25
              }} />
                  <span className="font-default text-xs tracking-[0.15em] uppercase text-muted-foreground">
                    {amenity.label}
                  </span>
                </div>;
          })}
          </div>

          <div className="mt-10">
            <span className="font-default font-bold text-primary" style={{
            fontSize: "clamp(1.75rem, 2.5vw, 2.25rem)"
          }}>
              From {room.price}
            </span>
            <span className="font-default text-sm text-muted-foreground ml-2">
              / project
            </span>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button asChild size="lg" className="rounded-lg font-default text-xs tracking-[0.2em] uppercase h-12 px-8 shadow-md bg-primary text-white hover:bg-primary/90">
              <Link to="/ContactUsPage">Get Started</Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="rounded-lg font-default text-xs tracking-[0.2em] uppercase h-12 px-8 border border-border hover:bg-accent">
              <Link to="/ContactUsPage">Learn More</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
function MetaItem({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div>
      <dt className="font-default text-[0.65rem] tracking-[0.25em] uppercase text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-2 font-default font-semibold text-lg text-foreground">
        {value}
      </dd>
    </div>;
}